/**
 * PlayerHeroCtaClusterView — desktop top-right CTA cluster (SE-5022).
 *
 * Renders the button cluster anchored to the top-right of the Player
 * Page hero:
 *   1. Secondary outline "+ Create" — Report / Note popover
 *   2. Primary solid "+ Add to Watchlist" / "Update list" — watchlist dropdown
 *   3. Optional meatball (⋯) — manual-player management menu (Edit /
 *      Transfer / Delete). Rendered only when `managementMenu` is
 *      provided; the V1.6 design places these actions under a meatball,
 *      not under "+ Create" (SE-5179).
 *
 * Pure presentation: receives each popover body as a ReactNode so the
 * container owns the data (watchlist query, mutations, create-modal).
 * @remarks View component — no business logic.
 */

import { memo } from "react";

import { Stack } from "@/components/atoms/Stack";
import {
  Popover,
  PopoverBackdrop,
  PopoverContent,
} from "@/components/atoms/Popover";

/** Public View props */
export interface PlayerHeroCtaClusterViewProps {
  /** Label for the primary CTA — switches to "Update list" once the player is in 1+ editable list */
  readonly primaryLabel: string;
  /** Whether the watchlist popover is open */
  readonly isWatchlistOpen: boolean;
  /** Open the watchlist popover */
  readonly onOpenWatchlist: () => void;
  /** Close the watchlist popover */
  readonly onCloseWatchlist: () => void;
  /** Whether the create popover is open */
  readonly isCreateOpen: boolean;
  /** Open the create popover */
  readonly onOpenCreate: () => void;
  /** Close the create popover */
  readonly onCloseCreate: () => void;
  /** Renders the secondary "+ Create" Popover trigger */
  readonly renderCreateTrigger: (
    triggerProps: Record<string, unknown>
  ) => React.JSX.Element;
  /** Renders the primary watchlist Popover trigger */
  readonly renderWatchlistTrigger: (
    triggerProps: Record<string, unknown>
  ) => React.JSX.Element;
  /** Body of the watchlist popover (rows + inline create) */
  readonly watchlistDropdown: React.ReactNode;
  /** Body of the create popover (Report / Note) */
  readonly createDropdown: React.ReactNode;
  /** Whether the meatball management popover is open */
  readonly isMenuOpen: boolean;
  /** Open the meatball management popover */
  readonly onOpenMenu: () => void;
  /** Close the meatball management popover */
  readonly onCloseMenu: () => void;
  /**
   * Renders the meatball (⋯) Popover trigger. Omitted together with
   * `managementMenu` when the player has no management actions.
   */
  readonly renderMenuTrigger?: (
    triggerProps: Record<string, unknown>
  ) => React.JSX.Element;
  /**
   * Body of the meatball management popover (Edit / Transfer / Delete).
   * When null/undefined the meatball is not rendered at all.
   */
  readonly managementMenu?: React.ReactNode;
  /**
   * Optional "Add to Pipeline" trigger (SE-5499). A plain button (not a
   * popover) that opens the reusable pick-pipeline-and-stage dialog owned by
   * the container. Rendered between the watchlist CTA and the meatball; omitted
   * when the surface does not offer the pipeline entry point.
   */
  readonly addToPipelineTrigger?: React.ReactNode;
}

/**
 * Render the two-CTA cluster.
 * @param props - View props
 * @param props.primaryLabel - Label for the watchlist CTA (unused here; consumed by the trigger renderer)
 * @param props.isWatchlistOpen - Watchlist popover open state
 * @param props.onOpenWatchlist - Watchlist popover open handler
 * @param props.onCloseWatchlist - Watchlist popover close handler
 * @param props.isCreateOpen - Create popover open state
 * @param props.onOpenCreate - Create popover open handler
 * @param props.onCloseCreate - Create popover close handler
 * @param props.renderCreateTrigger - Render fn for the secondary CTA trigger
 * @param props.renderWatchlistTrigger - Render fn for the primary CTA trigger
 * @param props.watchlistDropdown - Watchlist popover body
 * @param props.createDropdown - Create popover body
 * @param props.isMenuOpen - Meatball management popover open state
 * @param props.onOpenMenu - Meatball management popover open handler
 * @param props.onCloseMenu - Meatball management popover close handler
 * @param props.renderMenuTrigger - Render fn for the meatball (⋯) trigger
 * @param props.managementMenu - Meatball management popover body (Edit / Transfer / Delete)
 * @param props.addToPipelineTrigger - Optional "Add to Pipeline" trigger button (SE-5499)
 */
const PlayerHeroCtaClusterView = ({
  primaryLabel: _primaryLabel,
  isWatchlistOpen,
  onOpenWatchlist,
  onCloseWatchlist,
  isCreateOpen,
  onOpenCreate,
  onCloseCreate,
  renderCreateTrigger,
  renderWatchlistTrigger,
  watchlistDropdown,
  createDropdown,
  isMenuOpen,
  onOpenMenu,
  onCloseMenu,
  renderMenuTrigger,
  managementMenu,
  addToPipelineTrigger,
}: PlayerHeroCtaClusterViewProps) => (
  <Stack direction="horizontal" className="items-center gap-3">
    <Popover
      isOpen={isCreateOpen}
      onOpen={onOpenCreate}
      onClose={onCloseCreate}
      placement="bottom right"
      offset={8}
      size="sm"
      trigger={renderCreateTrigger}
    >
      <PopoverBackdrop className="bg-transparent" />
      <PopoverContent className="border-0 bg-transparent p-0 shadow-none">
        {createDropdown}
      </PopoverContent>
    </Popover>

    <Popover
      isOpen={isWatchlistOpen}
      onOpen={onOpenWatchlist}
      onClose={onCloseWatchlist}
      placement="bottom right"
      offset={8}
      size="md"
      trigger={renderWatchlistTrigger}
    >
      <PopoverBackdrop className="bg-transparent" />
      <PopoverContent className="border-0 bg-transparent p-0 shadow-none">
        {watchlistDropdown}
      </PopoverContent>
    </Popover>

    {addToPipelineTrigger}

    {managementMenu && renderMenuTrigger ? (
      <Popover
        isOpen={isMenuOpen}
        onOpen={onOpenMenu}
        onClose={onCloseMenu}
        placement="bottom right"
        offset={8}
        size="xs"
        trigger={renderMenuTrigger}
      >
        <PopoverBackdrop className="bg-transparent" />
        <PopoverContent className="border-0 bg-transparent p-0 shadow-none">
          {managementMenu}
        </PopoverContent>
      </Popover>
    ) : null}
  </Stack>
);

PlayerHeroCtaClusterView.displayName = "PlayerHeroCtaClusterView";

export default memo(PlayerHeroCtaClusterView);
